import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList, ElementRef, AfterViewInit, ViewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import { ItestListItem } from '@ispm/dashboard/test/interfaces/test-list-item';
import { DateHandler } from '@ispm/shared/utils/DateHandler';
import { map, take } from 'rxjs/operators';
import { VerticalTestActionsItemComponent } from '@ispm/test/components/vertical-test-actions/vertical-test-actions-item/vertical-test-actions-item.component';

export interface TestClickedEvent {
  testId: string;
  revisionId: string;
  status: string;
}

@Component({
  selector: 'ispm-vertical-test-actions-list',
  templateUrl: './vertical-test-actions-list.component.html',
  styleUrls: ['./vertical-test-actions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerticalTestActionsListComponent implements OnInit, AfterViewInit {
  testsList$: Observable<ItestListItem[]>;

  @ViewChildren(VerticalTestActionsItemComponent, { read: ElementRef }) testItems: QueryList<ElementRef<HTMLElement>>;

  @ViewChild('container', { static: false, read: ElementRef }) listContainer: ElementRef;

  @Input() set tests$(inputTests$: Observable<any[]>) {
    this.testsList$ = inputTests$.pipe(
      map(tests => tests.sort(this.sortTestListItemsByNewer)),
      map(tests => tests.map(
        test => test && test.revisions
          .sort(this.sortTestListItemsByNewer)
          .map(revision => this.generateNewRow(test.id, test.testNumber, test.name, revision, test.disease)))
      ),
      map((tests: ItestListItem[][]) => ([] as ItestListItem[]).concat(...tests)),
      map(tests => tests.filter(test => !!test))
    );
  }

  @Input() currentRevisionId: string;
  @Output() testClicked = new EventEmitter<TestClickedEvent>();

  constructor() {
  }

  ngOnInit() {
  }

  handleTestClicked(selectedTest) {
    if (this.currentRevisionId !== selectedTest) {
      this.testClicked.emit({
        testId: selectedTest.id,
        revisionId: selectedTest.revisionId,
        status: selectedTest.status
      });
    }
  }

  private sortTestListItemsByNewer(a: ItestListItem, b: ItestListItem) {
    const selectDate = (item: ItestListItem) => {
      if (!item || !item.meta) {
        return;
      }

      return item.meta.created ? item.meta.created : item.meta.lastUpdated;
    };

    const aDate = selectDate(a);
    const bDate = selectDate(b);

    return DateHandler.getTime(new Date(bDate)) - DateHandler.getTime(new Date(aDate));
  }

  private generateNewRow(id: string, testNumber: string, testType: string, revision: any, disease: string): ItestListItem {
    return {
      id,
      testType: testType,
      testNumber: testNumber,
      revision: revision.name,
      revisionId: revision.id,
      date: new Date(revision.datetime),
      status: revision.status,
      MRN: revision.MRN,
      numberTooltip: false,
      disease: disease
    };
  }

  ngAfterViewInit(): void {
    this.scrollActiveTestIntoView();
  }

  private scrollActiveTestIntoView(): void {
    this.testsList$.pipe(take(1)).subscribe(tests => {
      const activeIndex = tests.findIndex(test => test.revisionId === this.currentRevisionId);
      if (activeIndex < 0 || activeIndex >= this.testItems.length) {
        return;
      }
      const element: HTMLElement = this.testItems.find((e, index) => index === activeIndex)
        .nativeElement;
      const container: HTMLElement = this.listContainer.nativeElement;
      const newTop = element.offsetTop - container.offsetHeight;
      container.scrollTop = newTop;
    });
  }
}
